import React from 'react';
import { Dialog } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { STATUS_COLORS, STATUS_TINTS, STATUS_LABEL_KEYS } from './shared/appointmentStatus';

const SOURCE_NAMES = { web: 'Web', android: 'Android', ios: 'iOS', hermina_care: 'Hermina Care' };
const METHOD_NAMES = { afya: 'Afya', hinai: 'HINAI' };

// PE-436 quick view: the title is stated once, status and source sit beside the
// name as badges, the schedule gets its own tinted block, and the remaining
// facts are compact label/value cells — every field of the old dialog kept.
const AppointmentDialog = ({ open, onClose, appointment, labels = {} }) => {
  if (!appointment) return null;

  const attrs = appointment.attributes;
  const status = attrs.status;
  const isCanceled = status === 2;
  const initials = (attrs.name || '?')
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const createdDate = String(attrs.created_at).split(' - ').slice(0, 2).join(', ');
  const sourceBadge = [SOURCE_NAMES[attrs.source] || attrs.source, METHOD_NAMES[attrs.appointment_method]]
    .filter(Boolean)
    .join(' · ');

  const visit = (url) => (event) => {
    event.preventDefault();
    Turbolinks.visit(url);
  };

  const cell = (label, value) => (
    <div className="appt-qv__cell">
      <div className="appt-qv__label">{label}</div>
      <div className="appt-qv__value">{value}</div>
    </div>
  );

  const link = (url, text) => (
    <a href={url} className="appt-qv__link" onClick={visit(url)}>{text}</a>
  );

  const copyBtn = (value, ariaLabel) => value && (
    <button
      type="button"
      className="appt-qv__copy"
      title={labels.copy}
      aria-label={ariaLabel || labels.copy}
      onClick={(event) => {
        navigator.clipboard.writeText(String(value));
        const icon = event.currentTarget.firstChild;
        icon.className = 'zmdi zmdi-check';
        setTimeout(() => { icon.className = 'zmdi zmdi-copy'; }, 1200);
      }}
    >
      <i className="zmdi zmdi-copy" />
    </button>
  );

  const withCopy = (value, copyValue, ariaLabel) => (
    <span className="appt-qv__value-row">
      {value}
      {copyBtn(copyValue, ariaLabel)}
    </span>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth classes={{ paper: 'appt-qv' }}>
      <div className="appt-qv__head">
        <span className="appt-qv__avatar">
          {attrs.profile_avatar_url ? (
            <img src={attrs.profile_avatar_url} alt={attrs.name} className="appt-qv__avatar-img" />
          ) : (
            initials
          )}
        </span>
        <div className="appt-qv__titles">
          <div className="appt-qv__title-row">
            <span className="appt-qv__name">{attrs.name}</span>
            <span
              className="appt-qv__badge"
              style={{ backgroundColor: STATUS_TINTS[status] || STATUS_TINTS.default, color: STATUS_COLORS[status] || STATUS_COLORS.default }}
            >
              <i className="appt-qv__badge-dot" style={{ backgroundColor: STATUS_COLORS[status] || STATUS_COLORS.default }} />
              {labels[STATUS_LABEL_KEYS[status]] || 'Unknown'}
            </span>
            <span className="appt-qv__badge appt-qv__badge--source">{sourceBadge}</span>
          </div>
          <div className="appt-qv__sub">
            {`${labels.qv_appointment_id} ${attrs.id} · ${labels.qv_created} ${createdDate}`}
            {copyBtn(attrs.slug, labels.qv_copy_id)}
          </div>
        </div>
        <button type="button" className="appt-qv__close" onClick={onClose} aria-label={labels.qv_close}>
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="appt-qv__body">
        <div className="appt-qv__schedule">
          {cell(labels.qv_schedule_time, (
            <>
              {attrs.date}
              <br />
              {attrs.time_label || '-'}
            </>
          ))}
          {cell(labels.qv_hospital, link(attrs.hospital_url, attrs.hospital_name))}
          {cell(labels.qv_doctor, link(attrs.doctor_url, attrs.doctor_name))}
          {cell(labels.qv_queue, attrs.queue_number || '-')}
        </div>

        <div className="appt-qv__grid">
          {cell(labels.qv_speciality, attrs.doctor_speciality_name || '-')}
          {cell(labels.qv_mrn, attrs.medical_record_number
            ? withCopy(attrs.medical_record_number, attrs.medical_record_number, labels.qv_copy_mrn)
            : labels.no_mrn)}
          {cell(labels.qv_dob, attrs.dob)}
          {cell(labels.qv_payment, attrs.payment_method)}
        </div>

        <div className="appt-qv__grid">
          {cell(labels.qv_email, withCopy(link(attrs.account_url, attrs.email), attrs.email, labels.qv_copy_email))}
          {cell(labels.qv_phone, withCopy(attrs.mobile_phone, attrs.mobile_phone, labels.qv_copy_phone))}
          {cell(
            labels.qv_insurance,
            attrs.insurance_status ? (labels[`status_${attrs.insurance_status}`] || attrs.insurance_status) : labels.no_insurance
          )}
          {cell(labels.qv_estimation, attrs.estimation_time || '-')}
        </div>

        <div className="appt-qv__grid">
          {cell(labels.qv_unit, attrs.selected_unit_name || '-')}
          {attrs.profile_insurance_id && cell(labels.qv_insurance_name, attrs.insurance_name || '-')}
          {attrs.profile_insurance_id && cell(labels.qv_insurance_number, attrs.insurance_number || '-')}
        </div>

        {isCanceled && attrs.notes && (
          <div className="appt-qv__notes">
            <div className="appt-qv__notes-title">{labels.qv_cancel_notes}</div>
            <div className="appt-qv__notes-body">{attrs.notes}</div>
          </div>
        )}
      </div>

      <div className="appt-qv__footer">
        <button type="button" className="appt-qv__quiet-btn" onClick={onClose}>{labels.qv_close}</button>
        <button type="button" className="btn btn-primary appt-qv__open-btn" onClick={visit(attrs.url)}>
          <ArrowForwardIcon fontSize="small" className="mr-1" />
          {labels.qv_open}
        </button>
      </div>
    </Dialog>
  );
};

export default AppointmentDialog;
